/**
 * Portrait Service
 * 
 * Manages loading and accessing character portrait data from the image server.
 * Provides a singleton service that caches the portrait registry and provides
 * convenient methods for retrieving portrait information and URLs.
 */

import { imageServerConfig } from '../config/imageServer';
import type { Portrait, PortraitRegistry, PortraitUrls, PortraitVariantSize } from '../types/portrait';

export class PortraitService {
  private static instance: PortraitService;
  private registry: PortraitRegistry | null = null;
  private isLoading: boolean = false;
  private loadPromise: Promise<void> | null = null;

  private constructor() {}

  /**
   * Get the singleton instance of PortraitService
   */
  public static getInstance(): PortraitService {
    if (!PortraitService.instance) {
      PortraitService.instance = new PortraitService();
    }
    return PortraitService.instance;
  }

  /**
   * Load the portrait registry from the image server
   * @returns Promise that resolves when the registry is loaded
   */
  public async loadRegistry(): Promise<void> {
    // If already loaded, return immediately
    if (this.registry) {
      return Promise.resolve();
    }

    // If currently loading, return the existing promise
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    // Start loading
    this.isLoading = true;
    this.loadPromise = this.fetchRegistry();
    
    try {
      await this.loadPromise;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  /**
   * Fetch the registry from the server
   */
  private async fetchRegistry(): Promise<void> {
    try {
      const registryUrl = imageServerConfig.registryUrl;
      console.log('[PortraitService] Loading registry from:', registryUrl);
      
      let response: Response;
      try {
        response = await fetch(registryUrl);
        
        if (!response.ok) {
          console.warn(`Failed to load portrait registry: ${response.status} ${response.statusText}`);
          // Continue gracefully - portraits will fall back to placeholders
          return;
        }
      } catch (error) {
        // Network errors (like certificate issues) are caught here
        console.error('[PortraitService] Network error loading registry:', error);
        console.warn('[PortraitService] If you see CORS/certificate errors, please visit the image server URL directly in your browser to accept the certificate:');
        console.warn(`  ${imageServerConfig.baseUrl}/health`);
        // Continue gracefully - portraits will fall back to placeholders
        return;
      }

      const data = await response.json();
      this.registry = data as PortraitRegistry;
      
      console.log(`Portrait registry loaded: ${Object.keys(this.registry.portraits).length} portraits available`);
    } catch (error) {
      console.warn('Failed to load portrait registry:', error);
      // Continue gracefully - portraits will fall back to placeholders
    }
  }

  /**
   * Get a portrait by ID
   * @param portraitId - The ID of the portrait to retrieve
   * @returns The portrait object, or null if not found
   */
  public getPortrait(portraitId: string | undefined): Portrait | null {
    if (!portraitId) {
      return this.getPortrait('generic');
    }

    if (!this.registry) {
      console.warn('Portrait registry not loaded yet. Call loadRegistry() first.');
      return null;
    }
    
    const portrait = this.registry.portraits[portraitId];
    
    if (!portrait) {
      // If not found and not already trying 'generic', fall back to generic
      if (portraitId !== 'generic') {
        console.warn(`Portrait '${portraitId}' not found, falling back to 'generic'`);
        return this.getPortrait('generic');
      }
      
      console.error('Generic portrait not found in registry!');
      return null;
    }

    return portrait;
  }

  /**
   * Get the URLs for a portrait variant
   * @param portraitId - The ID of the portrait
   * @param variant - The variant size to retrieve
   * @returns Object with webp and jpg URLs, or null if portrait not found
   */
  public getPortraitUrls(portraitId: string | undefined, variant: PortraitVariantSize): PortraitUrls | null {
    // Use 'generic' if no portrait ID provided
    const effectivePortraitId = portraitId || 'generic';
    
    // Check if this is a namespaced ID (adventure-specific portrait)
    const isNamespaced = effectivePortraitId.includes(':');
    
    if (isNamespaced) {
      // For namespaced IDs (e.g., 'styling-exploration:art_director'),
      // construct URLs directly without checking the central registry
      // The image server will handle looking up adventure-specific portraits
      return {
        webp: `${imageServerConfig.baseUrl}/portraits/${effectivePortraitId}/${variant}`,
        jpg: `${imageServerConfig.baseUrl}/portraits/${effectivePortraitId}/${variant}`,
      };
    }
    
    // For non-namespaced IDs, use the smart format negotiation endpoint
    // This endpoint handles format negotiation (WebP vs JPEG) automatically
    // and works with the actual file structure on the server
    const baseUrl = imageServerConfig.baseUrl;
    const encodedId = encodeURIComponent(effectivePortraitId);
    
    // Use the smart format negotiation endpoint which handles both WebP and JPEG
    // The server will return the appropriate format based on browser support
    const variantUrl = `${baseUrl}/portraits/${encodedId}/${variant}`;
    
    return {
      webp: variantUrl, // Server will return WebP if supported
      jpg: variantUrl,  // Server will return JPEG as fallback
    };
  }

  /**
   * Get a list of all available portrait IDs
   * @returns Array of portrait IDs
   */
  public getAvailablePortraits(): string[] {
    if (!this.registry) {
      return [];
    }
    return Object.keys(this.registry.portraits);
  }

  /**
   * Check if the registry has been loaded
   * @returns True if the registry is loaded
   */
  public isLoaded(): boolean {
    return this.registry !== null;
  }

  /**
   * Check if a portrait exists in the registry
   * @param portraitId - The ID to check
   * @returns True if the portrait exists
   */
  public hasPortrait(portraitId: string): boolean {
    return this.registry !== null && portraitId in this.registry.portraits;
  }

  /**
   * Get the registry version
   * @returns The registry version string, or null if not loaded
   */
  public getVersion(): string | null {
    return this.registry?.version ?? null;
  }

  /**
   * Reset the service (useful for testing)
   */
  public reset(): void {
    this.registry = null;
    this.isLoading = false;
    this.loadPromise = null;
  }
}

// Export singleton instance
export const portraitService = PortraitService.getInstance();
export default portraitService;

